import { useState } from "react";
import SelectGroup from "../../../../components/Forms/SelectGroup/SelectGroup.tsx";
import SectionHeader from "../../../../components/SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../../../constants/formStyles.ts";
import { PURPOSE_OPTIONS } from "../../../../constants/vehiclePurposes.ts";
import { COMMON_ENGINE_TYPES, SELECTABLE_ENGINE_TYPES, ENGINE_TYPE_OPTIONS, isEngineType } from "../../../../constants/vehicleEngines.ts";
import type { CanonicalVehicleType } from "../../../../types/lifecycle.ts";
import { VEHICLE_TYPE_OPTIONS, type VehicleField } from "../model.ts";
import type { LifecycleController } from "../useLifecycle.ts";
import { CARD, InfoRow, Spinner } from "./shared.tsx";
import { RecordStatus } from "./RecordStatus.tsx";

const VEHICLE_EDITORS: {
  key: VehicleField;
  label: string;
  type?: "text" | "number";
}[] = [
  { key: "make", label: "Марка" },
  { key: "model", label: "Модел" },
  { key: "chassis", label: "Шаси" },
  { key: "productionYear", label: "Год. производство", type: "number" },
  { key: "seats", label: "Места", type: "number" },
  { key: "displacement", label: "Куб. см", type: "number" },
  { key: "enginePower", label: "Мощност (к.с.)", type: "number" },
  { key: "maxWeightF1", label: "Макс. маса F1", type: "number" },
  { key: "color", label: "Цвят" },
  { key: "engineType", label: "Вид двигател / гориво" },
];

export function QuoteEditor({
  controller,
}: {
  controller: LifecycleController;
}) {
  const {
    quoteForm,
    draft,
    plate,
    certNumber,
    pin,
    purpose,
    vehicleType,
    coverageStart,
    installments,
    current,
    busy,
    search,
    quote,
    missingRequired,
    error,
  } = controller;
  const setCertNumber = (v: string) => quoteForm.setInput("certNumber", v);
  const setSearchPin = (v: string) => quoteForm.setInput("pin", v);
  const setPurpose = (v: string) => quoteForm.setInput("purpose", v);
  const setVehicleType = (v: CanonicalVehicleType | "") =>
    quoteForm.setInput("vehicleType", v);
  const setCoverageStart = (v: string) =>
    quoteForm.setInput("coverageStart", v);
  const setInstallments = (v: number) => quoteForm.setInput("installments", v);

  // Persisted edits reach the draft after Wing acknowledges the save.
  const vehicle = draft?.vehicle ?? null;
  const [vehicleOpen, setVehicleOpen] = useState(true);
  const [showOtherEngines, setShowOtherEngines] = useState(false);
  const buffer = quoteForm.vehicleEdits?.values ?? null;
  const view = buffer ?? vehicle;
  const selectedEngine = view?.engineType;
  // A less common value returned by a lookup must remain visible and selected.
  const showAllEngines = showOtherEngines ||
    (isEngineType(selectedEngine) && !COMMON_ENGINE_TYPES.includes(selectedEngine));
  const record = controller.records.vehicle;
  const vehicleChanged = Boolean(buffer) || record.changed;
  const vehicleEdited = Object.keys(quoteForm.manual).some((k) =>
    k.startsWith("vehicle.")
  );
  const editVehicle = quoteForm.bufferVehicle;
  const discardVehicle = quoteForm.discardVehicle;
  const saveVehicle = controller.records.saveVehicle;
  const editor = ({
    key,
    label,
    type = "text",
  }: (typeof VEHICLE_EDITORS)[number]) => {
    const path = `vehicle.${key}`;
    const required = missingRequired.some((field) => field.path === path);
    const value = String(view?.[key] ?? "");
    return (
      <div key={key}>
        <label htmlFor={path} className={labelClass}>
          {label}
          {required && " — задължително"}
        </label>
        {key === "engineType" ? (<>
          <select
            id={path}
            className={inputClass}
            aria-invalid={
              required || error?.fields.some((field) => field.field === path)
            }
            value={isEngineType(value) ? value : ""}
            onChange={(e) => {
              setShowOtherEngines(false);
              editVehicle(key, e.target.value);
            }}
          >
            <option value="">— изберете —</option>
            {isEngineType(value) && !SELECTABLE_ENGINE_TYPES.includes(value) && (
              <option value={value} disabled>{ENGINE_TYPE_OPTIONS[value]} — уточнете</option>
            )}
            {(showAllEngines ? SELECTABLE_ENGINE_TYPES : COMMON_ENGINE_TYPES).map((id) => (
              <option key={id} value={id}>
                {ENGINE_TYPE_OPTIONS[id]}
              </option>
            ))}
          </select>
          {!showAllEngines && (
            <button
              type="button"
              className="mt-1 text-xs text-primary hover:underline"
              onClick={() => setShowOtherEngines(true)}
            >
              Покажи всички видове
            </button>
          )}
        </>
        ) : (
          <input
            id={path}
            type={type}
            className={inputClass}
            aria-invalid={
              required || error?.fields.some((field) => field.field === path)
            }
            value={value}
            onChange={(e) =>
              editVehicle(
                key,
                type === "number" ? Number(e.target.value) : e.target.value
              )
            }
          />
        )}
      </div>
    );
  };
  return (
    <>
      <div
        className={`grid grid-cols-1 items-stretch gap-4 ${
          draft ? "xl:grid-cols-2" : ""
        }`}
      >
        <fieldset
          disabled={controller.busy !== null || controller.records.saving}
          className={`${CARD} min-w-0`}
        >
          <SectionHeader title="Гражданска отговорност" />
          <div className="p-4 pt-2">
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <label htmlFor={"QuoteEditor-1"} className={labelClass}>
                  Регистрационен номер
                </label>
                <input
                  id={"QuoteEditor-1"}
                  type="text"
                  value={plate}
                  onChange={(e) => quoteForm.setInput("plate", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="CA1458BT"
                  className={`${inputClass} uppercase tracking-widest`}
                  autoComplete="off"
                />
              </div>
              <div>
                <label htmlFor={"QuoteEditor-2"} className={labelClass}>
                  № на свидетелство
                </label>
                <input
                  id={"QuoteEditor-2"}
                  type="text"
                  value={certNumber}
                  onChange={(e) => setCertNumber(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="000124799"
                  className={inputClass}
                  autoComplete="off"
                />
              </div>
              <div>
                <label htmlFor={"QuoteEditor-3"} className={labelClass}>
                  ЕГН / ЕИК на застраховащия{" "}
                  <span className="text-[10px] text-body dark:text-bodydark">
                    (по избор)
                  </span>
                </label>
                <input
                  id={"QuoteEditor-3"}
                  type="text"
                  value={pin}
                  onChange={(e) => setSearchPin(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="напр. 9208047184"
                  className={inputClass}
                  autoComplete="off"
                />
              </div>
              <div>
                <label htmlFor={"QuoteEditor-4"} className={labelClass}>
                  Предназначение{" "}
                  <span className="text-[10px] text-body dark:text-bodydark">
                    (по избор)
                  </span>
                </label>
                <SelectGroup
                  id={"QuoteEditor-4"}
                  value={purpose}
                  onChange={(v) => setPurpose(v)}
                  options={PURPOSE_OPTIONS}
                  placeholder="— автоматично —"
                />
              </div>
              <div>
                <label htmlFor={"QuoteEditor-5"} className={labelClass}>
                  Вид на превозното средство{" "}
                  <span className="text-[10px] text-body dark:text-bodydark">
                    (по избор)
                  </span>
                </label>
                <SelectGroup
                  id={"QuoteEditor-5"}
                  value={vehicleType}
                  onChange={(v) =>
                    setVehicleType(v as CanonicalVehicleType | "")
                  }
                  options={VEHICLE_TYPE_OPTIONS}
                  placeholder="— автоматично —"
                />
              </div>
              <div>
                <label htmlFor={"QuoteEditor-6"} className={labelClass}>
                  Начална дата{" "}
                  <span className="text-[10px] text-body dark:text-bodydark">
                    (по избор)
                  </span>
                </label>
                <input
                  id={"QuoteEditor-6"}
                  type="date"
                  value={coverageStart}
                  onChange={(e) => setCoverageStart(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <label
                  htmlFor={"QuoteEditor-7"}
                  className={`${labelClass} !mb-0`}
                >
                  Вноски
                </label>
                <select
                  id={"QuoteEditor-7"}
                  className={`${inputClass} !w-20 !py-1`}
                  value={installments}
                  onChange={(e) => setInstallments(Number(e.target.value))}
                >
                  {(current?.installments?.allowedCounts ?? [1, 2, 4, 12]).map(
                    (n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    )
                  )}
                </select>
              </div>
              <button
                onClick={search}
                disabled={
                  busy !== null ||
                  controller.records.saving ||
                  quoteForm.hasPendingEdits ||
                  !plate.trim() ||
                  !certNumber.trim()
                }
                className="flex shrink-0 items-center gap-1.5 rounded bg-primary px-4 py-2 text-sm font-semibold text-white transition-opacity hover:bg-opacity-90 disabled:opacity-50"
              >
                {busy === "quote" ? <Spinner /> : null}
                {busy === "quote"
                  ? "Търсене..."
                  : quote
                  ? "Преизчисли"
                  : "Търси"}
              </button>
            </div>
          </div>
        </fieldset>

        {draft && vehicle && (
          <fieldset
            disabled={controller.busy !== null || controller.records.saving}
            aria-label="Данни за превозното средство"
            className={`${CARD} min-w-0`}
          >
            <SectionHeader title="Данни за превозното средство" />
            <div className="p-4 pt-2">
              <button
                type="button"
                aria-expanded={vehicleOpen}
                onClick={() => setVehicleOpen((open) => !open)}
                className="flex w-full items-start justify-between gap-3 border-b border-stroke pb-3 text-left dark:border-strokedark"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-black dark:text-white">
                    {[vehicle.make, vehicle.model].filter(Boolean).join(" ") ||
                      "—"}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-body dark:text-bodydark">
                    {vehicle.chassis || plate}
                  </span>
                  {vehicleEdited && (
                    <span className="mt-1 block text-[10px] text-warning">
                      Променен
                    </span>
                  )}
                  {vehicleChanged && (
                    <span className="mt-1 block text-[10px] text-warning">
                      Незаписани промени
                    </span>
                  )}
                </span>
                <span className="shrink-0 rounded bg-gray-2 px-2 py-1 text-[10px] font-medium text-black dark:bg-meta-4 dark:text-white">
                  {vehicleOpen ? "Скрий" : "Редактирай"}
                </span>
              </button>
              <RecordStatus
                state={record}
                retry={controller.records.retryVehicle}
              />

              {vehicleOpen ? (
                <div className="grid grid-cols-1 gap-x-4 gap-y-2 pt-3 sm:grid-cols-2">
                  {VEHICLE_EDITORS.map(editor)}
                  <p className="text-xs text-body dark:text-bodydark sm:col-span-2">
                    Брой места, макс. маса F1, цвят и вид двигател се използват
                    само за тази заявка.
                  </p>
                  {vehicleChanged && (
                    <div className="flex justify-end gap-2 pt-1 sm:col-span-2">
                      {buffer && (
                        <button
                          type="button"
                          onClick={discardVehicle}
                          className="rounded bg-gray-2 px-3 py-1.5 text-xs font-medium text-black dark:bg-meta-4 dark:text-white"
                        >
                          Отказ
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={saveVehicle}
                        disabled={
                          record.loading ||
                          !record.loaded ||
                          Boolean(record.validation) ||
                          (!record.changed && Boolean(record.error))
                        }
                        className="rounded bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-opacity-90 disabled:opacity-50"
                      >
                        {record.changed ? "Запиши" : "Приложи"}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-3">
                  {VEHICLE_EDITORS.map(({ key, label }) => {
                    const value = vehicle[key];
                    return (
                      <InfoRow
                        key={key}
                        label={label}
                        value={
                          key === "engineType" && isEngineType(value)
                            ? ENGINE_TYPE_OPTIONS[value]
                            : value || "—"
                        }
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </fieldset>
        )}
      </div>
    </>
  );
}
